import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { CONSTANTS } from '../../data.js';
import { WorkbenchViewport } from '../../components/WorkbenchViewport';
import { WorkbenchItem } from '../../components/WorkbenchItem';
import { useZoomContext, useWorkbenchContext, useUIContext, useInventoryContext, useGameLoopContext, useGameDataContext, useGameStateContext } from '../../app/GameProviders';
import { useWorkbenchDrag } from '../../hooks/useWorkbenchDrag';
import { useCombinations } from '../../hooks/useCombinations';
import type { DragInfo } from '../../types';
import { isPointInRect, applyDropMoveAndMaybeDelete } from './placement';
import { Howl } from 'howler';
import { ASSETS } from '../../assetManifest.js';

interface Props {}

export function WorkbenchCanvas({}: Props) {
  const { scrollRef, contentRef, zoom, setZoom } = useZoomContext();
  const { workbenchMaterials, setWorkbenchMaterials, draggingInstanceId, getNextInstanceId, setCombinationResult } = useWorkbenchContext();
  const { inventory, setInventory } = useInventoryContext();
  const { inventoryPanelRef } = useUIContext();
  const { addToPantry, servingAreaRef, addToServingArea } = useGameLoopContext();
  const { MATERIALS, POSSIBLE_ORDERS, RECIPES, TRIPLE_RECIPES } = useGameDataContext();
  const { settings } = useGameStateContext();

  const { attemptCombine } = useCombinations({
    inventory,
    setInventory,
    setCombinationResult,
    getNextInstanceId,
    addToPantry,
  });

  // Helper function to check if an item is a valid servable dish
  const isServableDish = (itemId: string): boolean => {
    const material = MATERIALS[itemId];
    if (!material) return false;
    
    // Check if it's a component (recipe result) or if it's in the possible orders list
    const isComponent = material.category === 'component';
    const isInPossibleOrders = POSSIBLE_ORDERS.includes(itemId);
    const isRecipeResult = Object.values(RECIPES).includes(itemId) || Object.values(TRIPLE_RECIPES).includes(itemId);
    
    // Allow serving if it's a component, in possible orders, or a recipe result
    return isComponent || isInPossibleOrders || isRecipeResult;
  };

  // Function to play serving drop sound
  const playServingDropSound = () => {
    const sfxAssets = (ASSETS as any)?.sfx;
    if (!sfxAssets) return;

    const soundAsset = sfxAssets.serving_drop;
    if (!soundAsset || !soundAsset.path) return;

    const volume = (settings.sfxVolume / 100) * CONSTANTS.SOUND_EFFECTS.SERVING_DROP_VOLUME;
    
    const sound = new Howl({
      src: [soundAsset.path],
      volume: volume,
      preload: true,
    });

    sound.play();
  };

  const onDragEnd = (info: DragInfo, instanceId: string) => {
    setWorkbenchMaterials(currentMaterials => {
      const sc = scrollRef.current;
      const invEl = inventoryPanelRef.current;
      const servingEl = servingAreaRef.current;

      if (servingEl) {
        const servingRect = servingEl.getBoundingClientRect();
        if (isPointInRect(info.point, servingRect)) {
          const draggedItem = currentMaterials.find(m => m.instanceId === instanceId);
          if (draggedItem) {
            const material = MATERIALS[draggedItem.id];
            if (material && isServableDish(draggedItem.id)) {
              // Play serving drop sound
              playServingDropSound();
              
              addToServingArea(draggedItem.id);
              return currentMaterials.filter(i => i.instanceId !== instanceId);
            }
          }
        }
      }

      if (invEl) {
        const invRect = invEl.getBoundingClientRect();
        if (isPointInRect(info.point, invRect)) {
          return currentMaterials.filter(i => i.instanceId !== instanceId);
        }
      }

      const updated = applyDropMoveAndMaybeDelete(currentMaterials, instanceId, info, sc, zoom, CONSTANTS.WORKBENCH_ITEM_SIZE);
      if (!updated) return currentMaterials;

      const combined = attemptCombine(updated, instanceId);
      return combined;
    });
  };

  const { startWorkbenchDrag } = useWorkbenchDrag({ onDragEnd });

  return (
    <WorkbenchViewport
      scrollRef={scrollRef}
      contentRef={contentRef}
      onZoomChange={setZoom}
    >
      <AnimatePresence>
        {workbenchMaterials.map(material => {
          const isDraggingThis = draggingInstanceId === material.instanceId;
          if (isDraggingThis) return null;
          return (
            <WorkbenchItem
              key={material.instanceId}
              material={material}
              onPointerDown={(e) => startWorkbenchDrag(e, material.instanceId, material.id)}
            />
          );
        })}
      </AnimatePresence>
    </WorkbenchViewport>
  );
}
